// src/utils/FamilyDetailPdfExport.js
// Rapport patrimonial Family Office — branding FO, données réelles Supabase
// Sections : Vue d'ensemble · Membres · Collections · Inventaire · Conservation IA
//            Héritiers · Matching · Provenance · Preuves d'authenticité · Données successorales
//            Performance · Disclaimer

import jsPDF from "jspdf";
import { BRAND_TITLE } from "../lib/rebrand";
import { valuationFooter } from "./valuationDisclosure";

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
  black:  [26, 26, 26],
  gold:   [201, 169, 97],
  cream:  [249, 247, 244],
  lgold:  [245, 237, 214],
  lgray:  [232, 232, 232],
  mgray:  [102, 102, 102],
  dgray:  [68, 68, 68],
  white:  [255, 255, 255],
  green:  [45, 106, 79],
  amber:  [146, 64, 14],
  red:    [127, 29, 29],
};

// ─── Primitives ───────────────────────────────────────────────────────────────
const fill   = (pdf, c) => pdf.setFillColor(...c);
const stroke = (pdf, c) => pdf.setDrawColor(...c);
const tc     = (pdf, c) => pdf.setTextColor(...c);

function formatEur(v) {
  if (v == null || v === "" || isNaN(parseFloat(v))) return "—";
  const n = Math.round(parseFloat(v));
  // Manual format: avoid Intl narrow-no-break-space that jsPDF renders as "/"
  const abs = Math.abs(n);
  const formatted = abs.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return (n < 0 ? "-" : "") + formatted + " €";
}
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}
function fmtDateShort(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR");
}
function truncate(pdf, str, maxMm) {
  if (!str) return "—";
  const approxCharW = 7 * 0.45 / pdf.internal.scaleFactor;
  const max = Math.floor(maxMm / approxCharW);
  return str.length <= max ? str : str.substring(0, max - 1) + "…";
}

// ─── Layout helpers ───────────────────────────────────────────────────────────
const MT = 36, MB = 18;

function newPage(pdf, fop, fam, pageW) {
  pdf.addPage();
  drawHeader(pdf, fop, fam, pageW);
  return MT;
}
function checkPage(pdf, fop, fam, y, pageW, pageH, needed = 50) {
  return (y + needed > pageH - MB) ? newPage(pdf, fop, fam, pageW) : y;
}

// ─── Header ───────────────────────────────────────────────────────────────────
function drawHeader(pdf, fop, fam, pageW) {
  fill(pdf, C.black); pdf.rect(0, 0, pageW, 26, "F");
  const foName = fop?.company?.trim()
    || [fop?.first_name, fop?.last_name].filter(Boolean).join(" ")
    || "Family Office";
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(10); tc(pdf, C.white);
  pdf.text(foName, 12, 11);
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(7.5); tc(pdf, C.gold);
  pdf.text("Family Office · " + BRAND_TITLE, 12, 18);
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(8.5); tc(pdf, C.white);
  pdf.text(fam?.label || "Famille", pageW - 12, 11, { align: "right" });
  pdf.setFontSize(7); tc(pdf, C.gold);
  pdf.text(`Rapport du ${fmtDate(new Date().toISOString())}`, pageW - 12, 18, { align: "right" });
  fill(pdf, C.gold); pdf.rect(0, 26, pageW, 0.7, "F");
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function drawFooter(pdf, n, total, pageW, pageH) {
  fill(pdf, C.lgray); pdf.rect(0, pageH - 11, pageW, 11, "F");
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(6.5); tc(pdf, C.mgray);
  pdf.text("Confidentiel — " + BRAND_TITLE + " · artkiv.com", 12, pageH - 4.5);
  pdf.text(`Page ${n} / ${total}`, pageW - 12, pageH - 4.5, { align: "right" });
}

// ─── Section title ────────────────────────────────────────────────────────────
function secTitle(pdf, label, y, pageW) {
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(8.5); tc(pdf, C.mgray);
  pdf.text(label.toUpperCase(), 12, y);
  stroke(pdf, C.lgray); pdf.setLineWidth(0.25);
  pdf.line(12, y + 1.5, pageW - 12, y + 1.5);
  return y + 7;
}

// ─── KPI cards ────────────────────────────────────────────────────────────────
function drawKpis(pdf, kpis, y, pageW) {
  const w = (pageW - 24) / kpis.length;
  kpis.forEach((k, i) => {
    const x = 12 + i * w;
    fill(pdf, C.cream); pdf.rect(x, y, w - 2, 17, "F");
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(6.5); tc(pdf, C.mgray);
    pdf.text(k.label, x + 3, y + 5);
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(10); tc(pdf, C.black);
    pdf.text(String(k.value ?? "—"), x + 3, y + 13);
    if (k.sub) { pdf.setFont("helvetica", "normal"); pdf.setFontSize(6); tc(pdf, C.mgray); pdf.text(k.sub, x + 3, y + 16); }
  });
  return y + 21;
}

// ─── Table ────────────────────────────────────────────────────────────────────
function drawTable(pdf, headers, rows, y, pageW, colWidths, rowH = 6.5) {
  const tW = pageW - 24;
  fill(pdf, C.black); pdf.rect(12, y, tW, 7.5, "F");
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(7); tc(pdf, C.white);
  let x = 12;
  headers.forEach((h, i) => { pdf.text(h, x + 2, y + 5.5); x += colWidths[i]; });
  y += 7.5;
  rows.forEach((row, ri) => {
    if (ri % 2 === 0) { fill(pdf, C.cream); pdf.rect(12, y, tW, rowH, "F"); }
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(7); tc(pdf, C.dgray);
    x = 12;
    row.forEach((cell, ci) => {
      pdf.text(truncate(pdf, String(cell ?? "—"), colWidths[ci] - 4), x + 2, y + 4.5);
      x += colWidths[ci];
    });
    stroke(pdf, C.lgray); pdf.setLineWidth(0.1);
    pdf.line(12, y + rowH, pageW - 12, y + rowH);
    y += rowH;
  });
  return y + 3;
}

// ─── Info / Alert boxes ───────────────────────────────────────────────────────
function infoBox(pdf, txt, y, pageW) {
  const lines = pdf.splitTextToSize(txt, pageW - 36);
  const h = lines.length * 4.5 + 6;
  fill(pdf, C.lgold); pdf.rect(12, y, pageW - 24, h, "F");
  fill(pdf, C.gold);  pdf.rect(12, y, 1.5, h, "F");
  pdf.setFont("helvetica", "italic"); pdf.setFontSize(7.5); tc(pdf, C.black);
  pdf.text(lines, 17, y + 5.5);
  return y + h + 3;
}

function alertBox(pdf, title, body, y, pageW, color, bg) {
  const lines = body ? pdf.splitTextToSize(body, pageW - 36) : [];
  const h = lines.length * 3.8 + 12;
  fill(pdf, bg); pdf.rect(12, y, pageW - 24, h, "F");
  fill(pdf, color); pdf.rect(12, y, 1.5, h, "F");
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(7.5); tc(pdf, color);
  pdf.text(title, 16, y + 6);
  if (lines.length) {
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(7); tc(pdf, C.dgray);
    pdf.text(lines, 16, y + 11);
  }
  return y + h + 3;
}

// ─── Artwork value helper ─────────────────────────────────────────────────────
function artVal(a) {
  return parseFloat(a.current_estimated_value)
    || parseFloat(a.valuation_consensus)
    || parseFloat(a.valuation_estimated_value)
    || parseFloat(a.estimated_value)
    || parseFloat(a.value)
    || parseFloat(a.price_high)
    || parseFloat(a.acquisition_price)
    || 0;
}
function artAcq(a) {
  return parseFloat(a.acquisition_price) || parseFloat(a.price) || 0;
}

// ═════════════════════════════════════════════════════════════════════════════
// EXPORT PRINCIPAL
// ═════════════════════════════════════════════════════════════════════════════

/**
 * generateFamilyPdf
 * @param {object} foProfile      { first_name, last_name, company }
 * @param {object} family         { label, notes, fiscal_regime }
 * @param {Array}  familyClients  [{ client_id, role, is_primary, profile:{...} }]
 * @param {Array}  artworks       select(*) depuis Supabase artworks
 * @param {Array}  collections    [{ id, name, title }]
 * @param {Array}  beneficiaries  [{ name, relation, email, art_styles, emotions }]
 * @param {string} reportType     "overview" | "succession" | "philanthropy"
 */
export async function generateFamilyPdf({
  foProfile,
  family,
  familyClients = [],
  artworks = [],
  collections = [],
  beneficiaries = [],
  reportType = "overview",
}) {
  const pdf  = new jsPDF("p", "mm", "a4");
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();

  // ── Calculs globaux ────────────────────────────────────────────────────────
  const totalValue    = artworks.reduce((s, a) => s + artVal(a), 0);
  const totalPurchase = artworks.reduce((s, a) => s + artAcq(a), 0);
  const growth        = totalPurchase > 0
    ? ((totalValue - totalPurchase) / totalPurchase * 100).toFixed(1) : null;

  const withValuation    = artworks.filter(a =>
    a.valuation_consensus || a.valuation_estimated_value ||
    a.current_estimated_value || a.estimated_value || a.value
  ).length;
  const withBlockchain   = artworks.filter(a => a.certificate_blockchain === true).length;
  const withConservation = artworks.filter(a => a.condition_report?.trim()).length;
  const withCert         = artworks.filter(a => a.certificate_auth).length;

  const primaryClient = familyClients.find(c => c.is_primary) || familyClients[0];
  const primaryName   = (() => {
    const p = primaryClient?.profile || primaryClient;
    if (!p) return "—";
    const name = `${p.first_name || ""} ${p.last_name || ""}`.trim();
    return name || "—";
  })();

  const reportTitles = { overview: "Rapport patrimonial", succession: "Plan de transmission", philanthropy: "Stratégie de mécénat" };

  // ── PAGE 1 ─────────────────────────────────────────────────────────────────
  drawHeader(pdf, foProfile, family, pageW);
  let y = MT;

  pdf.setFont("helvetica", "bold"); pdf.setFontSize(15); tc(pdf, C.black);
  pdf.text(reportTitles[reportType] || "Rapport patrimonial", 12, y + 5);
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(8.5); tc(pdf, C.mgray);
  pdf.text(`Famille : ${family?.label || "—"}  ·  Contact principal : ${primaryName}`, 12, y + 12);
  y += 18;

  y = drawKpis(pdf, [
    { label: "Valeur estimée totale",  value: formatEur(totalValue) },
    { label: "Œuvres inventoriées",    value: artworks.length },
    { label: "Collections",            value: collections.length },
    { label: "Valorisations IA",       value: `${withValuation} / ${artworks.length}`, sub: withValuation < artworks.length ? `${artworks.length - withValuation} à analyser` : "Complet" },
    { label: "Preuves d'authenticité", value: withBlockchain, sub: withBlockchain < artworks.length ? `${artworks.length - withBlockchain} non certifiés` : "" },
  ], y, pageW);
  y += 4;

  // ── 1. MEMBRES ─────────────────────────────────────────────────────────────
  y = checkPage(pdf, foProfile, family, y, pageW, pageH, 40);
  y = secTitle(pdf, "1. Membres de la famille", y, pageW);
  const roleMap = { owner: "Propriétaire", spouse: "Conjoint", child: "Enfant", parent: "Parent", member: "Membre" };
  const memberRows = familyClients.map(fc => {
    const p = fc.profile || fc || {};
    return [
      `${p.first_name || ""} ${p.last_name || ""}`.trim() || "—",
      roleMap[fc.role] || fc.role || "—",
      fc.is_primary ? "Oui" : "—",
      fmtDateShort(fc.created_at),
    ];
  });
  y = drawTable(pdf, ["Nom", "Rôle", "Contact princ.", "Rattaché le"],
    memberRows.length ? memberRows : [["—","—","—","—"]], y, pageW, [70, 42, 30, 46]);
  y += 4;

  // ── 2. COLLECTIONS ─────────────────────────────────────────────────────────
  y = checkPage(pdf, foProfile, family, y, pageW, pageH, 40);
  y = secTitle(pdf, "2. Collections", y, pageW);
  const colRows = collections.map(col => {
    const arts = artworks.filter(a => a.collection_id === col.id);
    const v    = arts.reduce((s, a) => s + artVal(a), 0);
    return [col.name || col.title || "—", arts.length, formatEur(v), arts.filter(a => a.certificate_blockchain).length];
  });
  y = drawTable(pdf, ["Nom de la collection", "Œuvres", "Valeur est.", "Preuve"],
    colRows.length ? colRows : [["Aucune collection","—","—","—"]], y, pageW, [100, 22, 40, 26]);
  y += 4;

  // ── 3. INVENTAIRE ──────────────────────────────────────────────────────────
  y = checkPage(pdf, foProfile, family, y, pageW, pageH, 60);
  const sorted = [...artworks].sort((a, b) => artVal(b) - artVal(a)).slice(0, 15);
  y = secTitle(pdf, `3. Inventaire — ${artworks.length} œuvres (15 premières par valeur)`, y, pageW);
  const invRows = sorted.map(a => {
    const etat = a.condition_report?.trim()
      ? (a.condition_report.toLowerCase().match(/urgent|dégra|attention/) ? "⚠ Att." : "Bon")
      : "—";
    return [a.title || "Sans titre", a.artist || "—", a.year || "—",
      formatEur(artVal(a)), a.location || "—", etat];
  });
  y = drawTable(pdf, ["Titre", "Artiste", "Année", "Valeur est.", "Localisation", "État"],
    invRows.length ? invRows : [["—","—","—","—","—","—"]], y, pageW, [46, 36, 14, 28, 36, 28]);

  // ── 4. CONSERVATION IA ─────────────────────────────────────────────────────
  const artsCons = artworks.filter(a => a.condition_report?.trim());
  if (artsCons.length > 0) {
    y = checkPage(pdf, foProfile, family, y, pageW, pageH, 50);
    y += 3;
    y = secTitle(pdf, `4. Rapports de conservation IA (${withConservation} œuvres)`, y, pageW);

    artsCons.slice(0, 8).forEach(a => {
      y = checkPage(pdf, foProfile, family, y, pageW, pageH, 22);
      const isUrgent = /urgent|dégra|attention/i.test(a.condition_report);
      const color = isUrgent ? C.red : C.gold;
      const bg    = isUrgent ? [254, 226, 226] : C.lgold;
      const title = `${a.title || "Sans titre"}${a.artist ? " — " + a.artist : ""}`;

      fill(pdf, bg);  pdf.rect(12, y, pageW - 24, 14, "F");
      fill(pdf, color); pdf.rect(12, y, 1.5, 14, "F");
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(7.5); tc(pdf, color);
      pdf.text(title, 16, y + 5);
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(7); tc(pdf, C.dgray);
      const lines = pdf.splitTextToSize(a.condition_report.substring(0, 200), pageW - 36);
      pdf.text(lines[0] || "", 16, y + 10.5);
      y += 17;

      if (a.style_ai || a.keywords_ai?.length) {
        y = checkPage(pdf, foProfile, family, y, pageW, pageH, 10);
        fill(pdf, C.cream); pdf.rect(12, y, pageW - 24, 8, "F");
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(6.5); tc(pdf, C.mgray);
        if (a.style_ai) pdf.text(`Style IA : ${a.style_ai}`, 16, y + 3.5);
        if (a.keywords_ai?.length) {
          const kw = (Array.isArray(a.keywords_ai) ? a.keywords_ai : [a.keywords_ai]).slice(0, 6).join(" · ");
          pdf.text(`Mots-clés : ${kw}`, 16, y + 7);
        }
        y += 10;
      }
      y += 2;
    });
  }

  // ── 5. DOCUMENTS & TRAÇABILITÉ ─────────────────────────────────────────────
  y = checkPage(pdf, foProfile, family, y, pageW, pageH, 45);
  y += 3;
  y = secTitle(pdf, "5. Documents & traçabilité", y, pageW);
  y = drawTable(pdf,
    ["Type de document", "Disponibles", "Manquants"],
    [
      ["Certificats d'authenticité", withCert,         artworks.length - withCert],
      ["Factures d'achat",           artworks.filter(a => a.invoice).length, artworks.length - artworks.filter(a => a.invoice).length],
      ["Rapports de conservation IA", withConservation, artworks.length - withConservation],
      ["Preuves d'authenticité",     withBlockchain,   artworks.length - withBlockchain],
    ], y, pageW, [100, 28, 60]);

  // ── 6. HÉRITIERS ───────────────────────────────────────────────────────────
  if (beneficiaries.length > 0 && reportType !== "philanthropy") {
    y = checkPage(pdf, foProfile, family, y, pageW, pageH, 50);
    y += 3;
    y = secTitle(pdf, `6. Héritiers & bénéficiaires (${beneficiaries.length})`, y, pageW);
    const benRows = beneficiaries.map(b => {
      const styles = Array.isArray(b.art_styles) ? b.art_styles.slice(0, 3).join(", ") : (b.art_styles || "—");
      return [b.name || "—", b.relation || "—", b.email || "—", styles || "—"];
    });
    y = drawTable(pdf, ["Nom", "Lien", "Email", "Préférences artistiques"],
      benRows, y, pageW, [38, 26, 52, 72]);
    y += 2;
  }

  // ── 7. MATCHING PATRIMONIAL ────────────────────────────────────────────────
  if (beneficiaries.length > 0 && reportType === "succession") {
    y = checkPage(pdf, foProfile, family, y, pageW, pageH, 55);
    y += 2;
    y = secTitle(pdf, "7. Matching patrimonial — simulation de répartition", y, pageW);

    const lots = beneficiaries.map(b => {
      const styles = Array.isArray(b.art_styles) ? b.art_styles : [];
      const matched = artworks.filter(a => {
        const s = (a.style_ai || a.normalized_style || "").toLowerCase();
        return styles.some(st => s.includes(st.toLowerCase().substring(0, 6)));
      });
      const lotVal = matched.reduce((s, a) => s + artVal(a), 0);
      return {
        name: b.name || "—",
        hasQ: !!(b.art_styles && (Array.isArray(b.art_styles) ? b.art_styles.length > 0 : true)),
        count: matched.length,
        value: lotVal,
      };
    });

    const matched = lots.reduce((s, l) => s + l.value, 0);
    const unmatched = totalValue - matched;

    y = drawTable(pdf,
      ["Héritier", "Questionnaire", "Œuvres", "Valeur lot", "% collection"],
      [
        ...lots.map(l => [
          l.name,
          l.hasQ ? "Complété" : "En attente",
          l.count || "—",
          formatEur(l.value),
          totalValue > 0 ? `${((l.value / totalValue) * 100).toFixed(1)} %` : "—",
        ]),
        ["Lot non attribué", "—", "—", formatEur(unmatched),
          totalValue > 0 ? `${((unmatched / totalValue) * 100).toFixed(1)} %` : "—"],
      ], y, pageW, [45, 26, 18, 36, 28 + (188 - 45 - 26 - 18 - 36 - 28 > 0 ? 188 - 45 - 26 - 18 - 36 - 28 : 0)]);

    y += 2;
    y = infoBox(pdf,
      "La simulation de matching est basée sur les préférences artistiques renseignées. " +
      "Elle est indicative et ne constitue pas une proposition juridiquement opposable.",
      y, pageW);
  }

  // ── 8. PROVENANCE ──────────────────────────────────────────────────────────
  const artsProv = artworks.filter(a => a.acquisition_source || a.acquisition_date || a.purchase_city);
  if (artsProv.length > 0) {
    y = checkPage(pdf, foProfile, family, y, pageW, pageH, 50);
    y += 3;
    y = secTitle(pdf, `8. Historique de provenance (${artsProv.length} œuvres documentées)`, y, pageW);
    const provRows = artsProv.slice(0, 12).map(a => [
      a.title || "Sans titre",
      a.artist || "—",
      a.acquisition_type || "—",
      a.acquisition_source || "—",
      a.acquisition_date ? fmtDateShort(a.acquisition_date) : (a.year || "—"),
      [a.purchase_city, a.purchase_country].filter(Boolean).join(", ") || "—",
    ]);
    y = drawTable(pdf, ["Titre", "Artiste", "Type", "Source", "Date", "Lieu"],
      provRows, y, pageW, [38, 28, 18, 36, 22, 46]);
  }

  // ── 9. PREUVES D'AUTHENTICITÉ & CERTIFICATS ─────────────────────────────────
  const artsChain = artworks.filter(a => a.certificate_blockchain || a.certificate_auth || a.certificate_authenticity);
  if (artsChain.length > 0) {
    y = checkPage(pdf, foProfile, family, y, pageW, pageH, 50);
    y += 3;
    y = secTitle(pdf, "9. Certificats & preuves d'authenticité", y, pageW);
    const chainRows = artsChain.slice(0, 12).map(a => [
      a.title || "Sans titre",
      a.artist || "—",
      a.certificate_blockchain ? "Preuve num." : "Cert. auth.",
      a.certificate_authenticity ? String(a.certificate_authenticity).substring(0, 18) + "…" : (a.certificate_blockchain ? "Vérifiable" : "—"),
      fmtDateShort(a.last_valuation_date) || "—",
    ]);
    y = drawTable(pdf, ["Titre", "Artiste", "Type", "Référence", "Vérif."],
      chainRows, y, pageW, [50, 35, 22, 46, 35]);

    const uncertified = artworks.filter(a => !a.certificate_blockchain && !a.certificate_auth);
    if (uncertified.length > 0) {
      y = checkPage(pdf, foProfile, family, y, pageW, pageH, 18);
      y += 2;
      y = alertBox(pdf,
        `${uncertified.length} œuvre(s) sans certificat — action recommandée`,
        null, y, pageW, C.amber, [255, 251, 235]);
    }
  }

  // ── 10. DONNÉES SUCCESSORALES ──────────────────────────────────────────────
  if (reportType === "succession" || reportType === "overview") {
    y = checkPage(pdf, foProfile, family, y, pageW, pageH, 90);
    y += 3;
    y = secTitle(pdf, "10. Données pour le calcul successoral", y, pageW);

    y = infoBox(pdf,
      "Ce récapitulatif est transmis à titre informatif. Le calcul des droits de succession, " +
      "la vérification de la réserve héréditaire et les simulations fiscales relèvent exclusivement " +
      "de la compétence du notaire. " + BRAND_TITLE + " ne produit pas de calcul fiscal.",
      y, pageW);
    y += 2;

    // Valeur totale + régime fiscal
    const halfW = (pageW - 24) / 2;
    fill(pdf, C.cream); pdf.rect(12, y, halfW - 2, 20, "F");
    fill(pdf, C.lgold); pdf.rect(12 + halfW, y, halfW + 2, 20, "F");
    fill(pdf, C.gold);  pdf.rect(12, y, 1.5, 20, "F");

    pdf.setFont("helvetica", "normal"); pdf.setFontSize(6.5); tc(pdf, C.mgray);
    pdf.text("VALEUR TOTALE ESTIMÉE", 16, y + 5);
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); tc(pdf, C.black);
    pdf.text(formatEur(totalValue), 16, y + 14);

    const fiscalMap = {
      "FR_FORFAIT_6_5":     "France — Forfait 6,5%",
      "FR_PLUS_VALUE":      "France — Plus-value 19%",
      "UK_CGT_SIMPLE_20":   "Royaume-Uni — CGT 20%",
      "DE_PRIVATE_1Y_RULE": "Allemagne — Exonération > 1 an",
    };
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(6.5); tc(pdf, C.mgray);
    pdf.text("RÉGIME FISCAL RENSEIGNÉ", 16 + halfW, y + 5);
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(9); tc(pdf, C.black);
    pdf.text(fiscalMap[family?.fiscal_regime] || family?.fiscal_regime || "Non renseigné", 16 + halfW, y + 12);
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(6.5); tc(pdf, C.mgray);
    pdf.text("À vérifier avec le notaire", 16 + halfW, y + 17);
    y += 24;

    // Répartition héritiers
    if (beneficiaries.length > 0) {
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(7); tc(pdf, C.mgray);
      pdf.text("RÉPARTITION SUGGÉRÉE PAR HÉRITIER (matching " + BRAND_TITLE + " — non contractuelle)", 12, y);
      y += 5;

      const lots2 = beneficiaries.map(b => {
        const styles = Array.isArray(b.art_styles) ? b.art_styles : [];
        const matched = artworks.filter(a => {
          const s = (a.style_ai || a.normalized_style || "").toLowerCase();
          return styles.some(st => s.includes(st.toLowerCase().substring(0, 6)));
        });
        const val = matched.reduce((s, a) => s + artVal(a), 0);
        return [
          b.name || "—",
          b.art_styles?.length ? "Oui" : "En attente",
          formatEur(val),
          totalValue > 0 ? `${((val / totalValue) * 100).toFixed(1)} %` : "—",
          val > 0 ? "Calculé" : "Questionnaire requis",
        ];
      });
      y = drawTable(pdf, ["Héritier", "Questionnaire", "Valeur lot", "% coll.", "Statut"],
        lots2, y, pageW, [42, 26, 34, 20, 66]);
      y += 2;
    }

    // Points notaire
    y = checkPage(pdf, foProfile, family, y, pageW, pageH, 30);
    y = alertBox(pdf,
      "Points à vérifier obligatoirement avec le notaire",
      "1. Respect de la quotité disponible (réserve héréditaire des enfants)\n" +
      "2. Donations antérieures à réintégrer dans la masse successorale\n" +
      "3. Régime matrimonial applicable (communauté / séparation de biens)\n" +
      "4. Dettes et passif déductibles de l'actif successoral",
      y, pageW, C.gold, C.lgold);
  }

  // ── 11. PERFORMANCE ────────────────────────────────────────────────────────
  const artsPerfom = artworks.filter(a => artAcq(a) > 0 && artVal(a) > 0);
  if (artsPerfom.length > 0) {
    y = checkPage(pdf, foProfile, family, y, pageW, pageH, 55);
    y += 3;
    y = secTitle(pdf, "11. Performance — évolution des valorisations", y, pageW);

    const perfRows = [...artsPerfom]
      .sort((a, b) => (artVal(b) - artAcq(b)) / artAcq(b) - (artVal(a) - artAcq(a)) / artAcq(a))
      .slice(0, 10)
      .map(a => {
        const pct = ((artVal(a) - artAcq(a)) / artAcq(a) * 100).toFixed(1);
        return [
          a.title || "Sans titre",
          a.artist || "—",
          formatEur(artAcq(a)),
          formatEur(artVal(a)),
          `${pct > 0 ? "+" : ""}${pct} %`,
        ];
      });
    y = drawTable(pdf, ["Titre", "Artiste", "Prix achat", "Valeur actuelle", "Performance"],
      perfRows, y, pageW, [48, 34, 28, 36, 42]);

    if (growth !== null) {
      y += 2;
      fill(pdf, C.cream); pdf.rect(12, y, pageW - 24, 10, "F");
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(8); tc(pdf, C.black);
      pdf.text(`Performance globale : ${growth > 0 ? "+" : ""}${growth} %`, 16, y + 6.5);
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(6.5); tc(pdf, C.mgray);
      pdf.text(`Achat total : ${formatEur(totalPurchase)}  ·  Valeur actuelle : ${formatEur(totalValue)}`, 100, y + 6.5);
      y += 13;
    }
  }

  // ── DISCLAIMER ─────────────────────────────────────────────────────────────
  y = checkPage(pdf, foProfile, family, y, pageW, pageH, 25);
  y += 4;
  const d1 = valuationFooter(artworks, "fr") + " Les valorisations sont indicatives. Elles sont établies à partir des données " + BRAND_TITLE + " et de l'analyse " +
    "IA Vision. Elles ne constituent pas une expertise officielle au sens légal. Pour toute succession, donation " +
    "ou cession, l'intervention d'un commissaire-priseur habilité est indispensable.";
  const d2 = "Ce document est confidentiel et destiné exclusivement au propriétaire et aux tiers autorisés via " + BRAND_TITLE + ".";
  const dlines = pdf.splitTextToSize(d1 + " " + d2, pageW - 34);
  const dh = dlines.length * 4 + 6;
  fill(pdf, C.cream); pdf.rect(12, y, pageW - 24, dh, "F");
  fill(pdf, C.gold);  pdf.rect(12, y, 1.5, dh, "F");
  pdf.setFont("helvetica", "italic"); pdf.setFontSize(7); tc(pdf, C.mgray);
  pdf.text(dlines, 17, y + 5);

  // ── FOOTERS ────────────────────────────────────────────────────────────────
  const total = pdf.internal.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    pdf.setPage(p);
    drawFooter(pdf, p, total, pageW, pageH);
  }

  // ── SAVE ───────────────────────────────────────────────────────────────────
  const slug     = (family?.label || "famille").replace(/\s+/g, "_").toLowerCase();
  const typeSlug = { overview: "rapport", succession: "transmission", philanthropy: "mecenat" }[reportType] || "rapport";
  const today    = new Date().toISOString().slice(0, 10);
  pdf.save(`artkiv_${typeSlug}_${slug}_${today}.pdf`);
}